import React, { useState } from "react";
import { api } from "@/lib/api";
import { Search, Package, Clock, Check, XCircle } from "lucide-react";
import { toast } from "sonner";

const statusStyle = {
  paid: { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", Icon: Check },
  delivered: { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", Icon: Check },
  awaiting_payment: { label: "Awaiting payment", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", Icon: Clock },
  verifying: { label: "Verifying", cls: "bg-blue-500/15 text-blue-400 border-blue-500/20", Icon: Clock },
  pending: { label: "Processing", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", Icon: Clock },
  failed: { label: "Failed", cls: "bg-red-500/15 text-red-400 border-red-500/20", Icon: XCircle },
  cancelled: { label: "Cancelled", cls: "bg-white/5 text-kyro-mute border-white/10", Icon: XCircle },
};

export default function TrackOrderPage() {
  const [form, setForm] = useState({ order_id: "", email: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/track/${form.order_id}`, { params: { email: form.email } });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Not found");
    } finally { setLoading(false); }
  };

  const s = result ? (statusStyle[result.status] || statusStyle.pending) : null;
  const S = s?.Icon;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-14">
      <div className="mb-8">
        <div className="kyro-overline">Track order</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Where's my order?</h1>
        <p className="text-kyro-mute mt-3">Enter your order ID and the email you used at checkout.</p>
      </div>

      <form onSubmit={submit} className="kyro-card p-6 md:p-8 space-y-4">
        <input data-testid="track-order-id" required value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} placeholder="KYR_xxxxxxxxxxxx" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-[#FF4433]" />
        <input data-testid="track-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email used at checkout" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 outline-none focus:border-[#FF4433]" />
        <button data-testid="track-submit-btn" disabled={loading} className="kyro-btn-primary inline-flex items-center gap-2"><Search className="w-4 h-4" /> {loading ? "Looking..." : "Track order"}</button>
      </form>

      {result && (
        <div className="kyro-card p-6 md:p-8 mt-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="text-xs text-kyro-mute">Order</div>
              <div className="font-mono text-sm mt-1">{result.id}</div>
              <div className="text-xs text-kyro-faint mt-1">{new Date(result.created_at).toLocaleString()}</div>
            </div>
            {s && <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border ${s.cls}`}><S className="w-3 h-3" /> {s.label}</span>}
          </div>
          <div className="space-y-2">
            {result.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-white/5 overflow-hidden shrink-0">
                  {it.product_image && <img src={it.product_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{it.product_name}</p>
                  <p className="text-xs text-kyro-mute">{it.plan_label}</p>
                </div>
                <p className="text-sm font-semibold">₹{it.price.toFixed(0)}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4">
            <p className="text-sm text-kyro-mute">Total</p>
            <p className="font-display font-black">₹{result.total.toFixed(0)}</p>
          </div>
          {result.delivery_notes && result.status === "paid" && (
            <div className="mt-5 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-2">Delivery details</div>
              <p className="text-sm whitespace-pre-wrap">{result.delivery_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
